import { useEffect, useMemo, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { Loader2, TrendingDown, TrendingUp } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import {
  getFuturesEmaSignals,
  type FuturesEmaSignalRow,
  type FuturesEmaTimeframe,
} from "@/lib/api/market-signals";

const TIMEFRAMES: Array<{ id: FuturesEmaTimeframe; label: string }> = [
  { id: "5m", label: "5m" },
  { id: "10m", label: "10m" },
  { id: "15m", label: "15m" },
  { id: "30m", label: "30m" },
  { id: "1h", label: "1h" },
  { id: "4h", label: "4h" },
  { id: "1d", label: "1d" },
];

type Signal = "buy" | "sell" | "flat" | "waiting";

export function EmaSignalCard({
  symbol,
}: {
  symbol: string;
}): ReactNode {
  const [rows, setRows] = useState<FuturesEmaSignalRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const refresh = async () => {
      try {
        setError(null);
        const next = await getFuturesEmaSignals(symbol);
        if (cancelled) return;
        setRows(next?.rows ?? null);
      } catch (err) {
        if (cancelled) return;
        setRows(null);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    void refresh();
    const timer = window.setInterval(() => void refresh(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [symbol]);

  const byTimeframe = useMemo(() => {
    const map = new Map<FuturesEmaTimeframe, FuturesEmaSignalRow>();
    for (const row of rows ?? []) map.set(row.timeframe, row);
    return map;
  }, [rows]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-card/70"
    >
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-2.5 py-1.5">
        <div>
          <p className="text-xs font-semibold text-foreground">
            EMA <span className="text-accent">Direction</span>
          </p>
          <p className="font-mono text-[10px] text-muted-foreground">
            {symbol.toUpperCase()} · 100/200
          </p>
        </div>
        {loading && (
          <Loader2
            aria-label="Loading EMA signals"
            className="size-3.5 animate-spin text-accent"
          />
        )}
      </div>

      <div className="flex max-h-[268px] flex-1 flex-col overflow-y-auto divide-y divide-border/35">
        {TIMEFRAMES.map((tf) => {
          const row = byTimeframe.get(tf.id);
          const signal = signalFor(row);
          return (
            <div
              key={tf.id}
              className="grid grid-cols-[44px_minmax(0,1fr)_52px] items-center gap-2 px-2.5 py-1.5"
            >
              <span className="font-mono text-[11px] font-semibold text-foreground">
                {tf.label}
              </span>
              <div className="min-w-0 truncate text-right font-mono text-[10px] tabular-nums">
                {row?.ema100 != null && row.ema200 != null ? (
                  <>
                    <span className="text-foreground">{fmt(row.ema100)}</span>
                    <span className="px-0.5 text-muted-foreground">/</span>
                    <span className="text-muted-foreground">{fmt(row.ema200)}</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">Waiting</span>
                )}
              </div>
              <SignalBadge signal={signal} />
            </div>
          );
        })}
      </div>

      {(error || rows === null) && !loading && (
        <p
          aria-live="polite"
          className="border-t border-border/40 px-2.5 py-1.5 text-[10px] text-muted-foreground"
        >
          {error
            ? "EMA feed error."
            : "Needs 200 closed candles."}
        </p>
      )}
    </motion.div>
  );
}

function signalFor(row: FuturesEmaSignalRow | undefined): Signal {
  if (!row || row.ema100 == null || row.ema200 == null) return "waiting";
  if (row.ema100 > row.ema200) return "buy";
  if (row.ema200 > row.ema100) return "sell";
  return "flat";
}

function SignalBadge({ signal }: { signal: Signal }): ReactNode {
  const label =
    signal === "buy"
      ? "Buy"
      : signal === "sell"
        ? "Sell"
        : signal === "flat"
          ? "Flat"
          : "Wait";
  const Icon = signal === "sell" ? TrendingDown : TrendingUp;

  return (
    <span
      className={cn(
        "inline-flex justify-center rounded-md px-1.5 py-0.5 text-[9px] font-semibold",
        signal === "buy" && "bg-emerald-500/10 text-emerald-500",
        signal === "sell" && "bg-destructive/10 text-destructive",
        signal === "flat" && "bg-muted text-muted-foreground",
        signal === "waiting" && "bg-muted/60 text-muted-foreground",
      )}
    >
      <span className="inline-flex items-center gap-1">
        {signal !== "waiting" && <Icon className="size-2.5" />}
        {label}
      </span>
    </span>
  );
}

function fmt(value: number): string {
  return value.toLocaleString("en-US", {
    maximumFractionDigits: value >= 100 ? 1 : 3,
  });
}
